import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './Map.css';
import ResortDetail from './ResortDetail';
import skiData from '../services/ski-data';

// 使用环境变量或默认值设置token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1Ijoiemhhb3poZW5oYW8iLCJhIjoiY203OTRzdzFuMDFkODJxcHBxY2JzYzVsYiJ9.F2ogSKhZ9rFkE7v2KcpjIA';

const Map = forwardRef(({ 
  markers, 
  selectedResort, 
  onResortSelect, 
  onMapMove, 
  onMapLoad, 
  onMapDragStart, 
  type,
  center,
  onLocationUpdate  // 添加位置更新回调
}, ref) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const initialLoadRef = useRef(true);

  // 将 createMarker 函数移到这里
  const createMarker = useCallback((markerData) => {
    const el = document.createElement('div');
    el.className = 'marker';
    
    // 根据排名设置不同的样式
    if (markerData.rank <= 3) {
      el.classList.add(`top-${markerData.rank}`);
    }
    
    // 添加排名数字
    const rankDiv = document.createElement('div');
    rankDiv.className = 'marker-rank';
    rankDiv.textContent = markerData.rank;
    el.appendChild(rankDiv);

    const marker = new mapboxgl.Marker(el)
      .setLngLat([markerData.longitude, markerData.latitude])
      .setPopup(
        new mapboxgl.Popup({ offset: 25 })
          .setHTML(markerData.content.replace(/\n/g, '<br>'))
      );

    return marker;
  }, []);

  // 暴露地图方法给父组件
  useImperativeHandle(ref, () => ({
    getBounds: () => {
      if (map.current) {
        const bounds = map.current.getBounds();
        return {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest()
        };
      }
      return null;
    },
    getMap: () => map.current
  }));

  // 初始化地图
  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/outdoors-v12',
      center: center || [100, 35],
      zoom: 3,
      minZoom: 3,
      maxZoom: 18
    });

    // 添加控件
    map.current.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
    map.current.addControl(new mapboxgl.ScaleControl());
    map.current.addControl(new mapboxgl.FullscreenControl());

    map.current.on('load', () => {
      setMapLoaded(true);
      onMapLoad?.();
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // 处理地图移动和拖拽事件
  useEffect(() => {
    if (!map.current) return;

    const moveEndHandler = () => {
      if (mapLoaded) {
        onMapMove?.();
      }
    };

    const dragStartHandler = () => {
      onMapDragStart?.();
    };

    map.current.on('moveend', moveEndHandler);
    map.current.on('dragstart', dragStartHandler);

    return () => {
      if (map.current) {
        map.current.off('moveend', moveEndHandler);
        map.current.off('dragstart', dragStartHandler);
      }
    };
  }, [mapLoaded, onMapMove, onMapDragStart]);

  // 添加新的 useEffect 来处理 center 的更新
  useEffect(() => {
    if (map.current && center) {
      map.current.setCenter(center);
    }
  }, [center]);

  // 添加新的 useEffect 来处理 type 相关的更新
  useEffect(() => {
    if (!map.current) return;

    // 移除之前的点击事件监听器
    map.current.off('click');

    // 根据新的 type 重新添加点击事件
    if (type === 'nearby') {
      map.current.on('click', (e) => {
        const features = map.current.queryRenderedFeatures(e.point, {
          layers: ['markers']
        });

        if (features.length === 0) {
          const { lng, lat } = e.lngLat;
          onLocationUpdate?.({
            longitude: lng,
            latitude: lat
          });
        }
      });
    }
  }, [type, onLocationUpdate]);

  // 处理标记点击事件
  const handleMarkerClick = async (marker) => {
    try {
      // 获取滑雪场详细信息
      const detail = await skiData.getResortDetail(marker.id);
      onResortSelect(detail);
    } catch (error) {
      console.error('Failed to fetch resort detail:', error);
      // 如果获取详情失败，至少显示基本信息
      onResortSelect(marker);
    }
  };

  // 更新标记
  useEffect(() => {
    if (!mapLoaded || !map.current || !markers?.length) return;

    // 清除现有标记
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // 创建标记
    markers.forEach(marker => {
      if (!marker.longitude || !marker.latitude) return;

      let mapMarker;
      if (type === 'nearby') {
        mapMarker = createMarker(marker);
      } else {
        // 其他类型的标记保持原有逻辑
        const el = document.createElement('div');
        switch (type) {
          case 'ranking':
            el.className = `resort-marker ranking-marker ${marker.isSelected ? 'selected' : ''}`;
            if (marker.rank <= 3) {
              el.setAttribute('data-rank', marker.rank);
            }
            el.textContent = marker.rank;
            break;
          case 'favorites':
            el.className = `resort-marker favorite-marker ${marker.isSelected ? 'selected' : ''}`;
            // 添加收藏图标
            const icon = document.createElement('span');
            icon.className = 'favorite-icon';
            icon.innerHTML = '★';
            el.appendChild(icon);
            break;
          case 'weather':
            el.className = 'resort-marker weather-marker';
            // 检查是否为室内滑雪场
            if (marker.isIndoor) {
              el.classList.add('indoor');
            } else {
              // 直接使用计算好的 level 值
              el.classList.add(`level-${marker.level}`);
            }
            break;
          default:
            el.className = 'resort-marker';
        }
        mapMarker = new mapboxgl.Marker(el)
          .setLngLat([marker.longitude, marker.latitude]);

        // 如果有弹出窗口内容，添加弹出窗口
        if (marker.popup) {
          mapMarker.setPopup(
            new mapboxgl.Popup({ offset: 25 })
              .setHTML(marker.popup)
          );
        }
      }

      mapMarker.addTo(map.current);

      // 修改点击事件处理
      mapMarker.getElement().addEventListener('click', (e) => {
        e.stopPropagation();
        handleMarkerClick(marker);
      });

      markersRef.current.push(mapMarker);
    });

    // 只在第一次加载时调整视图
    if (initialLoadRef.current && markers.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      markers.forEach(marker => {
        if (marker.longitude && marker.latitude) {
          bounds.extend([marker.longitude, marker.latitude]);
        }
      });

      if (!bounds.isEmpty()) {
        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 10,
          duration: 1000
        });
      }
      initialLoadRef.current = false;
    }
  }, [markers, type, mapLoaded, onResortSelect, createMarker]);

  // 添加处理小程序按钮点击的函数
  const handleMiniProgramClick = () => {
    // 创建一个包含小程序二维码的弹窗
    const modal = document.createElement('div');
    modal.className = 'mini-program-modal';
    modal.innerHTML = `
      <div class="mini-program-content">
        <div class="mini-program-close">&times;</div>
        <h3>扫码使用小程序</h3>
        <img src="/qrcode.jpg" alt="小程序二维码" class="qrcode-image" />
        <p>请使用微信扫描二维码</p>
      </div>
    `;

    // 添加关闭功能
    modal.addEventListener('click', (e) => {
      if (e.target.className === 'mini-program-modal' || 
          e.target.className === 'mini-program-close') {
        document.body.removeChild(modal);
      }
    });

    document.body.appendChild(modal);
  };

  return (
    <div className="map-view">
      <div ref={mapContainer} className="map-container" />
      {selectedResort && type === 'nearby' && (
        <ResortDetail 
          resort={selectedResort}
          onClose={() => onResortSelect(null)}
        />
      )}
      <div className="bottom-right-info">
        <div className="info-container">
          <button 
            className="mini-program-btn"
            onClick={handleMiniProgramClick}
          >
            <span className="mini-program-icon">使用小程序</span>
          </button>
          <a 
            href="https://beian.miit.gov.cn/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="icp-link"
          >
            京ICP备2025103740号
          </a>
        </div>
      </div>
    </div>
  );
});

export default Map; 